import { Calculator, Mail, MessageCircle, Info, Send } from 'lucide-react'
import { formatearPrecio, formatearNumero } from '../lib/pricing-tiers'

interface ServicioActivo {
  label: string
  volumen: number
  precioUnitario: number
  precioBase: number
  subtotal: number
}

interface PricingSummaryProps {
  serviciosActivos: ServicioActivo[]
  precioMensual: number
  precioTotal: number
  totalEnvios: number
  meses: number
  setMeses: (m: number) => void
  onSolicitar: () => void
}

function getServiceIcon(label: string) {
  if (label === 'Correo') return <Mail className="h-[18px] w-[18px] text-teal-700" />
  if (label === 'WhatsApp') return <MessageCircle className="h-[18px] w-[18px] text-teal-500" />
  return (
    <>
      <Mail className="h-3.5 w-3.5 text-teal-700" />
      <MessageCircle className="h-3.5 w-3.5 text-teal-500" />
    </>
  )
}

function getVolumeColor(label: string) {
  if (label === 'Correo') return 'text-teal-700'
  if (label === 'WhatsApp') return 'text-teal-500'
  return 'text-teal-900'
}

export function PricingSummary({
  serviciosActivos,
  precioMensual,
  precioTotal,
  totalEnvios,
  meses,
  setMeses,
  onSolicitar,
}: PricingSummaryProps) {
  return (
    <div
      className="rounded-lg p-6 mb-6"
      style={{
        background: 'linear-gradient(to right, #f0fdfa, transparent)',
        border: '1px solid #0d9488',
      }}
    >
      {/* Title */}
      <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
        <Calculator className="w-5 h-5 text-teal-500 mr-2" />
        Resumen de Cotización
      </h2>

      {/* Services Breakdown - Always visible */}
      <div className="mb-4 space-y-3">
        {/* Correo Service */}
        {renderServiceCard(
          'Correo',
          serviciosActivos.find((s) => s.label === 'Correo')
        )}
        {/* WhatsApp Service */}
        {renderServiceCard(
          'WhatsApp',
          serviciosActivos.find((s) => s.label === 'WhatsApp')
        )}
        {/* Combined Service */}
        {renderServiceCard(
          'Correo + WhatsApp',
          serviciosActivos.find((s) => s.label === 'Correo + WhatsApp')
        )}
      </div>

      {/* Pricing Summary */}
      <div className="bg-white rounded-lg p-4 shadow-sm mb-3">
        <div className="flex justify-between items-center mb-3">
          <span className="text-gray-600">Total envíos:</span>
          <span className="font-semibold text-gray-900">{formatearNumero(totalEnvios)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Total mensual:</span>
          <span className="font-semibold text-gray-900">{formatearPrecio(precioMensual)}</span>
        </div>
      </div>

      {/* Total with months */}
      <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-gray-600">{meses === 1 ? 'Mes:' : 'Meses:'}</span>
            <select
              value={meses}
              onChange={(e) => setMeses(Number(e.target.value))}
              className="border border-gray-300 dark:border-gray-700 rounded px-3 py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div className="text-right">
            <div
              className="text-4xl font-bold bg-clip-text text-transparent"
              style={{
                backgroundImage: 'linear-gradient(to right, #0d9488, #2dd4bf)',
              }}
            >
              {formatearPrecio(precioTotal)}
            </div>
            <span className="text-xs text-gray-500">USD total</span>
          </div>
        </div>
      </div>

      {/* CTA Button */}
      <button
        onClick={onSolicitar}
        className="w-full inline-flex items-center justify-center px-4 py-3 bg-teal-800 hover:bg-teal-900 text-white text-sm lg:text-base font-medium rounded-lg transition-colors duration-200 shadow-sm"
      >
        <Send className="w-4 h-4 mr-2" />
        Solicitar Cotización
      </button>

      {/* Additional Info */}
      <div className="mt-4 p-4 bg-white rounded-lg shadow-sm">
        <p className="text-xs text-gray-600 text-center">
          <Info className="w-4 h-4 inline-block text-teal-500 mr-1" />
          Los precios varían según el volumen seleccionado
          <br />
          Sin costos ocultos ni comisiones adicionales
        </p>
      </div>
    </div>
  )
}

function renderServiceCard(label: string, servicio?: ServicioActivo) {
  const tieneDescuento = servicio && servicio.precioUnitario < servicio.precioBase

  return (
    <div className="p-4 rounded-lg bg-white shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {getServiceIcon(label)}
          <span className="font-semibold text-gray-900">{label}</span>
        </div>
        <span className={`text-sm font-bold ${getVolumeColor(label)}`}>
          {servicio ? formatearNumero(servicio.volumen) : '0'}
        </span>
      </div>
      <div className="flex justify-between items-center text-sm">
        <span className="text-gray-600">Precio unitario:</span>
        <div className="flex items-center gap-2">
          {tieneDescuento && (
            <span className="text-xs text-gray-500 line-through">
              {formatearPrecio(servicio!.precioBase)}
            </span>
          )}
          <span className="font-semibold text-gray-900">
            {servicio ? formatearPrecio(servicio.precioUnitario) : '$0,00'}
          </span>
        </div>
      </div>
      <div className="flex justify-between items-center text-sm">
        <span className="text-gray-600">Subtotal:</span>
        <span className="font-semibold text-gray-900">
          {servicio ? formatearPrecio(servicio.subtotal) : '$0,00'}
        </span>
      </div>
    </div>
  )
}
